/**
 * Corner Mode Strategy
 *
 * Display mode strategy for corner-positioned popups.
 * Corner popups appear in one of the four corners of the viewport.
 */

import type { DisplayConfig, CornerPosition } from '../../shared/types/popup';
import { BaseDisplayModeStrategy } from './base-strategy';

/**
 * Map corner positions to CSS class suffixes.
 */
const POSITION_SUFFIX_MAP: Record<CornerPosition, string> = {
	bottom_right: 'br',
	bottom_left: 'bl',
	top_right: 'tr',
	top_left: 'tl',
};

/**
 * Strategy for corner display mode.
 */
export class CornerModeStrategy extends BaseDisplayModeStrategy {
	private positionSuffix: string;

	constructor(position: CornerPosition) {
		super();
		this.positionSuffix = POSITION_SUFFIX_MAP[position] || 'br';
	}

	/**
	 * Returns CSS classes for corner mode.
	 * Includes both the base corner class and position-specific class.
	 */
	public getDisplayModeClasses = (): string[] => {
		return ['gp-popup--corner', `gp-popup--corner-${this.positionSuffix}`];
	};

	/**
	 * Apply body scroll lock when popup opens.
	 * Only applies scroll lock if backdrop is shown.
	 * Overrides base to add conditional logic based on showBackdrop.
	 * @param config The display configuration
	 */
	public override applyBodyState(config?: DisplayConfig): void {
		// Only add scroll lock if backdrop is shown
		// When backdrop is disabled, users should be able to scroll the page
		if (config?.showBackdrop !== false) {
			super.applyBodyState(config);
		}
	}

	/**
	 * Apply custom sizing to the dialog.
	 * Corner popups support width/height customization.
	 */
	public applyDialogSizing = (
		dialog: HTMLElement,
		config: DisplayConfig
	): void => {
		dialog.style.width = config.popupWidth;
		dialog.style.height = config.popupHeight;
	};

	/**
	 * Corner popups respect the closeOnBackdropClick config.
	 */
	public shouldCloseOnBackdropClick = (config: DisplayConfig): boolean => {
		return config.closeOnBackdropClick;
	};
}
